import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { DraggableEvent, DraggableData } from 'react-draggable'

export interface Size {
  width: number
  height: number
}

export interface Position {
  x: number
  y: number
}

export interface UseDraggableResizableOptions {
  size: Size
  onSizeChange?: (size: Size) => void
  position?: Position | null
  onPositionChange?: (position: Position) => void
  minWidth?: number
  minHeight?: number
  anchorEl?: Element | null
  open: boolean
  onResizeEnd?: (size: Size) => void
  onDragEnd?: (position: Position) => void
  disabled?: boolean
}

export interface UseDraggableResizableResult {
  currentSize: Size
  currentPosition: Position
  isDragging: boolean
  paperRef: React.RefObject<HTMLDivElement>
  handleDragStart: (e: DraggableEvent) => false | void
  handleDragStop: (e: DraggableEvent, data: DraggableData) => void
  handleResizeStart: (e: React.PointerEvent) => void
  viewportSize: Size
  bounds: { left: number; top: number; right: number; bottom: number }
  isResizing: boolean
  isResizingRef: React.MutableRefObject<boolean>
  blockInteractionRef: React.MutableRefObject<boolean>
}

export const useDraggableResizable = ({
  size,
  onSizeChange,
  position: controlledPosition,
  onPositionChange,
  minWidth = 400,
  minHeight = 300,
  anchorEl,
  open,
  onResizeEnd,
  onDragEnd,
  disabled = false,
}: UseDraggableResizableOptions): UseDraggableResizableResult => {
  const paperRef = useRef<HTMLDivElement>(null)
  const isResizingRef = useRef(false)
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 })
  const blockInteractionRef = useRef(false)

  const [internalSize, setInternalSize] = useState(size)
  const [isDragging, setIsDragging] = useState(false)
  const [internalPosition, setInternalPosition] = useState<Position | null>(controlledPosition ?? null)
  const [viewportSize, setViewportSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  })

  // Use controlled or internal state
  const currentSize = onSizeChange ? size : internalSize
  const currentInternalPosition = onPositionChange ? controlledPosition : internalPosition

  // Sync internal size with prop when it changes externally
  useEffect(() => {
    if (!onSizeChange) {
      setInternalSize((prev) => {
        // Only update if values actually changed
        if (prev.width !== size.width || prev.height !== size.height) {
          return size
        }
        return prev
      })
    }
  }, [size.width, size.height, onSizeChange])

  // Calculate initial position based on anchorEl
  const calculateInitialPosition = useCallback((): Position => {
    if (!anchorEl) {
      return { x: viewportSize.width / 2 - currentSize.width / 2, y: 100 }
    }

    const rect = anchorEl.getBoundingClientRect()

    // Position below the anchor, centered horizontally
    let x = rect.left + rect.width / 2 - currentSize.width / 2
    let y = rect.bottom + 12

    // Constrain to viewport
    const maxX = Math.max(0, viewportSize.width - currentSize.width)
    const maxY = Math.max(0, viewportSize.height - currentSize.height)

    x = Math.max(0, Math.min(maxX, x))
    y = Math.max(0, Math.min(maxY, y))

    return { x, y }
  }, [anchorEl, currentSize.width, currentSize.height, viewportSize])

  // Get current position (calculated or set)
  const currentPosition = useMemo(() => {
    if (currentInternalPosition) return currentInternalPosition
    return calculateInitialPosition()
  }, [currentInternalPosition, calculateInitialPosition])

  // Bounds for dragging
  const bounds = useMemo(
    () => ({
      left: 0,
      top: 0,
      right: Math.max(0, viewportSize.width - currentSize.width),
      bottom: Math.max(0, viewportSize.height - currentSize.height),
    }),
    [viewportSize, currentSize],
  )

  // Drag handlers
  const handleDragStart = useCallback(
    (e: DraggableEvent): false | void => {
      if (disabled) return false
      const target = (e as MouseEvent).target as HTMLElement
      if (target.closest('button')) {
        return false
      }

      // If no position is set yet, capture the current element position
      // This happens when the element is initially positioned by Popper
      if (!currentInternalPosition && paperRef.current) {
        const rect = paperRef.current.getBoundingClientRect()
        const initialPos = { x: rect.left, y: rect.top }
        if (onPositionChange) {
          onPositionChange(initialPos)
        } else {
          setInternalPosition(initialPos)
        }
      }

      setIsDragging(true)
      blockInteractionRef.current = true
    },
    [disabled, currentInternalPosition, onPositionChange],
  )

  const handleDragStop = useCallback(
    (_e: DraggableEvent, data: DraggableData) => {
      const newPosition = { x: data.x, y: data.y }
      if (onPositionChange) {
        onPositionChange(newPosition)
      } else {
        setInternalPosition(newPosition)
      }
      setIsDragging(false)
      onDragEnd?.(newPosition)

      setTimeout(() => {
        blockInteractionRef.current = false
      }, 100)
    },
    [onPositionChange, onDragEnd],
  )

  // Resize handlers
  const handleResizeStart = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return
      isResizingRef.current = true
      blockInteractionRef.current = true
      resizeStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        width: currentSize.width,
        height: currentSize.height,
      }
      e.preventDefault()
      e.stopPropagation()
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    },
    [currentSize, disabled],
  )

  const handleResizeMove = useCallback(
    (e: PointerEvent) => {
      if (!isResizingRef.current) return

      const deltaX = e.clientX - resizeStartRef.current.x
      const deltaY = e.clientY - resizeStartRef.current.y

      const newWidth = Math.max(minWidth, resizeStartRef.current.width + deltaX)
      const newHeight = Math.max(minHeight, resizeStartRef.current.height + deltaY)

      const element = paperRef.current
      let constrainedWidth: number
      let constrainedHeight: number

      if (element) {
        const rect = element.getBoundingClientRect()
        const maxWidth = window.innerWidth - rect.left
        const maxHeight = window.innerHeight - rect.top

        constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth))
        constrainedHeight = Math.max(minHeight, Math.min(maxHeight, newHeight))
      } else {
        constrainedWidth = Math.max(minWidth, Math.min(window.innerWidth, newWidth))
        constrainedHeight = Math.max(minHeight, Math.min(window.innerHeight, newHeight))
      }

      const newSize = { width: constrainedWidth, height: constrainedHeight }
      if (onSizeChange) {
        onSizeChange(newSize)
      } else {
        setInternalSize(newSize)
      }
    },
    [minWidth, minHeight, onSizeChange],
  )

  const handleResizeEnd = useCallback(() => {
    if (isResizingRef.current) {
      onResizeEnd?.(currentSize)
    }
    isResizingRef.current = false

    setTimeout(() => {
      blockInteractionRef.current = false
    }, 100)
  }, [currentSize, onResizeEnd])

  useEffect(() => {
    if (!open) return

    const handlePointerMove = (e: PointerEvent) => {
      if (isResizingRef.current) {
        handleResizeMove(e)
      }
    }

    const handlePointerUp = () => {
      handleResizeEnd()
    }

    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', handlePointerUp)

    return () => {
      document.removeEventListener('pointermove', handlePointerMove)
      document.removeEventListener('pointerup', handlePointerUp)
    }
  }, [open, handleResizeMove, handleResizeEnd])

  // Store refs for values needed in window resize handler to avoid dependency issues
  const currentSizeRef = useRef(currentSize)
  const currentInternalPositionRef = useRef(currentInternalPosition)
  const onSizeChangeRef = useRef(onSizeChange)
  const onPositionChangeRef = useRef(onPositionChange)

  useEffect(() => {
    currentSizeRef.current = currentSize
  }, [currentSize])

  useEffect(() => {
    currentInternalPositionRef.current = currentInternalPosition
  }, [currentInternalPosition])

  useEffect(() => {
    onSizeChangeRef.current = onSizeChange
  }, [onSizeChange])

  useEffect(() => {
    onPositionChangeRef.current = onPositionChange
  }, [onPositionChange])

  // Handle window resize - constrain size and position to viewport
  useEffect(() => {
    if (!open) return

    const handleWindowResize = () => {
      const maxWidth = window.innerWidth
      const maxHeight = window.innerHeight

      setViewportSize({ width: maxWidth, height: maxHeight })

      const size = currentSizeRef.current

      // Constrain size to viewport
      let newWidth = size.width
      let newHeight = size.height
      let sizeChanged = false

      if (size.width > maxWidth) {
        newWidth = Math.max(minWidth, maxWidth)
        sizeChanged = true
      }
      if (size.height > maxHeight) {
        newHeight = Math.max(minHeight, maxHeight)
        sizeChanged = true
      }

      if (sizeChanged) {
        const newSize = { width: newWidth, height: newHeight }
        if (onSizeChangeRef.current) {
          onSizeChangeRef.current(newSize)
        } else {
          setInternalSize(newSize)
        }
      }

      // Constrain position to viewport
      const currentPos = currentInternalPositionRef.current || { x: maxWidth / 2 - newWidth / 2, y: 100 }
      const constrainedX = Math.max(0, Math.min(maxWidth - newWidth, currentPos.x))
      const constrainedY = Math.max(0, Math.min(maxHeight - newHeight, currentPos.y))

      if (constrainedX !== currentPos.x || constrainedY !== currentPos.y) {
        const newPosition = { x: constrainedX, y: constrainedY }
        if (onPositionChangeRef.current) {
          onPositionChangeRef.current(newPosition)
        } else {
          setInternalPosition(newPosition)
        }
      }
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [open, minWidth, minHeight])

  return {
    currentSize,
    currentPosition,
    isDragging,
    paperRef,
    handleDragStart,
    handleDragStop,
    handleResizeStart,
    viewportSize,
    bounds,
    isResizing: isResizingRef.current,
    isResizingRef,
    blockInteractionRef,
  }
}

export default useDraggableResizable
